import moment from "moment-timezone";
import type { SalonService } from "../../../../types/salon";
import type { GetTransactionsResponse } from "../../../../types/transactions";
import {
  ACCEPTED_UNCONFIRMED_COLOR,
  getAppointmentStatusColor,
} from "../../../../types/calendar";
import { parseApiDateTime } from "../../../../utils/timeUtils";
import { ServiceRow, staffServiceFor } from "./bookingTypes";

/** Appointment statuses that can never be rescheduled (rebook still can). */
export const RESCHEDULE_BLOCKED_STATUSES: ReadonlySet<string> = new Set([
  "Completed",
  "Cancelled",
  "No Show",
  "Declined",
]);

/**
 * Reschedule moves an existing appointment, so it is offered for any
 * appointment with booked services that is not in a finished status. Past
 * appointments stay reschedulable, and the new time may be in the past too.
 */
export const canReschedule = (
  txn: GetTransactionsResponse | null | undefined,
): boolean => {
  if (!txn) return false;
  const status = txn.transaction.appointment_status;
  if (status && RESCHEDULE_BLOCKED_STATUSES.has(status)) return false;
  return (txn.line_items?.booked_services || []).length > 0;
};

/**
 * The first service row is the appointment's primary service: booking,
 * rebook and reschedule all require it, so it can never be removed.
 */
export const canDeleteServiceRow = (rows: ServiceRow[], uid: number): boolean =>
  rows.length > 1 && rows[0].uid !== uid && rows.some((r) => r.uid === uid);

/**
 * Whether `staffId` can perform `svc`. No service or provider chosen yet
 * imposes no restriction.
 */
export const isStaffQualified = (
  svc: SalonService | undefined,
  staffId: string,
): boolean => !svc || !staffId || !!staffServiceFor(svc, staffId);

/** Distinct providers one appointment may carry. */
export const MAX_STAFF_PER_APPOINTMENT = 2;

/** Distinct chosen providers across the rows (unset rows count as nobody). */
export const distinctStaff = (rows: ServiceRow[]): string[] => {
  const ids: string[] = [];
  rows.forEach((r) => {
    const id = r.employeeId;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
};

/** An appointment carries at most two distinct providers. */
export const withinStaffLimit = (rows: ServiceRow[]): boolean =>
  distinctStaff(rows).length <= MAX_STAFF_PER_APPOINTMENT;

/**
 * Whether `staffId` may be picked for row `uid` without exceeding the
 * provider limit (the other rows' providers are fixed).
 */
export const canAssignStaff = (
  rows: ServiceRow[],
  uid: number,
  staffId: string,
): boolean => {
  if (!staffId) return true;
  const others = distinctStaff(rows.filter((r) => r.uid !== uid));
  return others.includes(staffId) || others.length < MAX_STAFF_PER_APPOINTMENT;
};

/** The part of a transaction the Arrived gate reads (its service starts). */
export interface ArrivedGateTransaction {
  line_items?: {
    booked_services?: ReadonlyArray<{ start_dt?: string | null }> | null;
  } | null;
}

/** How early (minutes before start) an appointment may be marked Arrived. */
export const ARRIVED_EARLY_WINDOW_MINUTES = 60;

/** Shown wherever the Arrived option is offered but not yet allowed. */
export const ARRIVED_UNAVAILABLE_REASON =
  "Available on the appointment day, starting 1 hour before start";

/** The earliest booked-service start as a salon-local moment, or null. */
const earliestServiceStart = (
  txn: ArrivedGateTransaction | null | undefined,
  timezone: string,
): moment.Moment | null => {
  const starts = (txn?.line_items?.booked_services || [])
    .map((s) => s.start_dt)
    .filter((dt): dt is string => !!dt)
    .map((dt) => parseApiDateTime(dt, timezone))
    .filter((m) => m.isValid());
  return starts.length === 0 ? null : moment.min(starts);
};

/**
 * Whether an appointment may be moved to Arrived right now: it starts on the
 * current salon day (salon timezone, not the browser's) and `now` is no
 * earlier than 1 hour before its start. There is no upper limit after start,
 * so late arrivals can still be marked. The start is the earliest booked
 * service start. Compares true instants (API datetimes are UTC).
 */
export const canMarkArrived = (
  txn: ArrivedGateTransaction | null | undefined,
  timezone: string,
  now: Date = new Date(),
): boolean => {
  const start = earliestServiceStart(txn, timezone);
  if (!start) return false;
  const salonNow = moment(now).tz(timezone);
  if (!start.isSame(salonNow, "day")) return false;
  return !salonNow.isBefore(
    start.clone().subtract(ARRIVED_EARLY_WINDOW_MINUTES, "minutes"),
  );
};

/**
 * Why `status` can't be picked for this appointment right now, or null when
 * it can. Only Arrived is gated (see `canMarkArrived`); status menus and the
 * card board call this rather than re-implementing the check.
 */
export const statusPickBlockedReason = (
  status: string,
  txn: ArrivedGateTransaction | null | undefined,
  timezone: string,
  now: Date = new Date(),
): string | null =>
  status === "Arrived" && !canMarkArrived(txn, timezone, now)
    ? ARRIVED_UNAVAILABLE_REASON
    : null;

/**
 * Whether an appointment belongs in the card board's Unconfirmed column, which
 * replaces the Accepted column: exactly the Accepted appointments (salons that
 * need confirmation put online bookings in Accepted until the client confirms
 * from the reminder text). A missing status is not unconfirmed.
 */
export const isUnconfirmedStatus = (status: string | null | undefined) =>
  status === "Accepted";

/** How close to its start (hours) an Accepted appointment reads as unconfirmed. */
export const AWAITING_CONFIRMATION_WINDOW_HOURS = 48;

/** The part of a transaction the display status reads. */
export interface StatusDisplayTransaction extends ArrivedGateTransaction {
  transaction?: { appointment_status?: string | null } | null;
}

/**
 * Whether `status` on this appointment is Accepted and its earliest booked
 * service starts within 48 hours of `now` (or has already started). Display
 * only: there is no such backend status.
 */
const acceptedNearStart = (
  status: string | null | undefined,
  txn: ArrivedGateTransaction | null | undefined,
  timezone: string,
  now: Date,
): boolean => {
  if (status !== "Accepted") return false;
  const start = earliestServiceStart(txn, timezone);
  if (!start) return false;
  return (
    start.diff(moment(now), "milliseconds") <=
    AWAITING_CONFIRMATION_WINDOW_HOURS * 60 * 60 * 1000
  );
};

/**
 * Whether an appointment is still Accepted (the client hasn't confirmed from
 * the reminder text) with its earliest service starting within 48 hours, or
 * already started. Compares true instants (API datetimes are UTC).
 */
export const isAwaitingConfirmation = (
  txn: StatusDisplayTransaction | null | undefined,
  timezone: string,
  now: Date = new Date(),
): boolean =>
  acceptedNearStart(txn?.transaction?.appointment_status, txn, timezone, now);

/** Label shown for an Accepted appointment awaiting confirmation. */
export const ACCEPTED_UNCONFIRMED_LABEL = "Accepted (Unconfirmed)";

/** How a status pill renders: label + Mantine color (name or "name.shade"). */
export interface StatusDisplay {
  label: string;
  color: string;
  /** True for the display-only "Accepted (Unconfirmed)" state. */
  awaitingConfirmation: boolean;
}

/**
 * The label + color a status pill shows. `status` is the status being shown
 * (it may be an optimistic one); an Accepted appointment within 48 hours of
 * start shows as "Accepted (Unconfirmed)" in deep orange, everything else as
 * its own status and `getAppointmentStatusColor`. Status menus still list
 * and write the real statuses.
 */
export const appointmentStatusDisplay = (
  status: string | null | undefined,
  txn: ArrivedGateTransaction | null | undefined,
  timezone: string,
  now: Date = new Date(),
): StatusDisplay =>
  acceptedNearStart(status, txn, timezone, now)
    ? {
        label: ACCEPTED_UNCONFIRMED_LABEL,
        color: ACCEPTED_UNCONFIRMED_COLOR,
        awaitingConfirmation: true,
      }
    : {
        label: status ?? "",
        color: getAppointmentStatusColor(status),
        awaitingConfirmation: false,
      };
